import { useState } from "react";
import { Eye, Lock, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const HiddenFSBODeals = ({ onJoinClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Unique AI-generated images with SEO-friendly geo-targeted naming
  const hiddenDeals = [
    {
      type: "Off-Market",
      city: "Plano",
      price: "$489,000",
      beds: 4,
      baths: 3,
      sqft: "2,400",
      status: "Motivated Seller",
      image: "https://static.prod-images.emergentagent.com/jobs/04f43ebb-3226-457d-8b8e-07cf7137d9f0/images/3e8108c5537e63be7ea0dabe041edce7ad735951307a00331392fb093f19df76.png",
      alt: "Plano Texas 4 bedroom modern home for sale by owner"
    },
    {
      type: "Pocket Listing",
      city: "Southlake",
      price: "$825,000",
      beds: 5,
      baths: 4,
      sqft: "3,800",
      status: "Exclusive Access",
      image: "https://static.prod-images.emergentagent.com/jobs/04f43ebb-3226-457d-8b8e-07cf7137d9f0/images/fa24dafb67709b921704dd90cd4537391ae4c035e75427db6cf0e682a0c0a485.png",
      alt: "Southlake Texas 5 bedroom luxury estate home for sale"
    },
    {
      type: "Coming Soon",
      city: "McKinney",
      price: "$399,000",
      beds: 3,
      baths: 2.5,
      sqft: "2,100",
      status: "Pre-Market",
      image: "https://static.prod-images.emergentagent.com/jobs/04f43ebb-3226-457d-8b8e-07cf7137d9f0/images/202e39b3d5eaccda3ed39db5358b41ce61435619144e7b2f914d49b56b7ba819.png",
      alt: "McKinney Texas 3 bedroom craftsman home for sale by owner"
    },
    {
      type: "Off-Market",
      city: "Frisco",
      price: "$675,000",
      beds: 4,
      baths: 3.5,
      sqft: "3,200",
      status: "Owner Moving",
      image: "https://static.prod-images.emergentagent.com/jobs/04f43ebb-3226-457d-8b8e-07cf7137d9f0/images/3df6e61dd07cf0d0deedbd6cc42a492badfa7364e6a99234008ad5a1ed912629.png",
      alt: "Frisco Texas 4 bedroom contemporary home for sale"
    },
    {
      type: "Pocket Listing",
      city: "Allen",
      price: "$535,000",
      beds: 4,
      baths: 3,
      sqft: "2,800",
      status: "Must Sell Fast",
      image: "https://static.prod-images.emergentagent.com/jobs/04f43ebb-3226-457d-8b8e-07cf7137d9f0/images/37e8a1a7c1e6255a49446fab8081f5623dc56385e84815188a7ba70ad0731674.png",
      alt: "Allen Texas 4 bedroom traditional family home for sale"
    },
    {
      type: "Coming Soon",
      city: "Prosper",
      price: "$720,000",
      beds: 5,
      baths: 4,
      sqft: "3,500",
      status: "New Construction",
      image: "https://static.prod-images.emergentagent.com/jobs/04f43ebb-3226-457d-8b8e-07cf7137d9f0/images/6f932d8acca9cb7fa82ad70148b94e0010464bbcdaa03cd9c6206ea0aea17dfb.png",
      alt: "Prosper Texas 5 bedroom new construction luxury home"
    }
  ];

  const nextDeal = () => {
    setCurrentIndex((prev) => (prev + 1) % hiddenDeals.length);
  };

  const prevDeal = () => {
    setCurrentIndex((prev) => (prev - 1 + hiddenDeals.length) % hiddenDeals.length);
  };

  const visibleDeals = [
    hiddenDeals[currentIndex],
    hiddenDeals[(currentIndex + 1) % hiddenDeals.length],
    hiddenDeals[(currentIndex + 2) % hiddenDeals.length]
  ];

  return (
    <div className="bg-gradient-to-r from-purple-900 to-blue-900 rounded-2xl p-8 shadow-2xl text-white">
      <div className="text-center mb-8">
        <div className="inline-flex items-center space-x-2 bg-yellow-400 text-purple-900 px-4 py-2 rounded-full font-bold mb-4">
          <Sparkles className="w-5 h-5" />
          <span>EXCLUSIVE ACCESS</span>
        </div>
        <h2 className="text-4xl font-extrabold mb-2">
          🔥 Hidden FSBO Deals
        </h2>
        <p className="text-xl text-purple-200">
          Off-market homes you won&apos;t find anywhere else
        </p>
      </div>

      {/* Carousel */}
      <div className="relative">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {visibleDeals.map((deal, index) => (
            <Card 
              key={index} 
              className="bg-white/10 backdrop-blur-sm border-2 border-white/20 overflow-hidden hover:border-yellow-400 transition-all group cursor-pointer"
              onClick={onJoinClick}
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={deal.image} 
                  alt={deal.alt}
                  title={`${deal.city} Texas ${deal.beds} bedroom home for sale - $${deal.price}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="text-center">
                    <Lock className="w-12 h-12 text-white mb-2 mx-auto" />
                    <p className="text-white font-bold">Click to Unlock</p>
                  </div>
                </div>
                <div className="absolute top-2 left-2 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                  {deal.type}
                </div>
                <div className="absolute top-2 right-2 bg-yellow-400 text-purple-900 px-3 py-1 rounded-full text-sm font-bold">
                  {deal.status}
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-2xl font-bold text-white">{deal.price}</h3>
                  <span className="text-purple-200 font-semibold">{deal.city}</span>
                </div>
                <div className="flex items-center space-x-4 text-sm text-purple-200">
                  <span>{deal.beds} beds</span>
                  <span>•</span>
                  <span>{deal.baths} baths</span>
                  <span>•</span>
                  <span>{deal.sqft} sqft</span>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevDeal}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm p-3 rounded-full transition-all"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <button
          onClick={nextDeal}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm p-3 rounded-full transition-all"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* CTA */}
      <div className="text-center space-y-4">
        <p className="text-xl font-bold text-yellow-300">
          <Eye className="inline w-5 h-5 mr-2" />
          17 more hidden deals available this week
        </p>
        <Button
          onClick={onJoinClick}
          className="bg-yellow-400 hover:bg-yellow-300 text-purple-900 font-extrabold text-xl py-6 px-12 rounded-xl shadow-2xl transform hover:scale-105 transition-all"
        >
          Get Early Access – Join Free →
        </Button>
        <p className="text-sm text-purple-200">
          No credit card required • Instant access • Cancel anytime
        </p>
      </div>
    </div>
  );
};

export default HiddenFSBODeals;
